import { useState, type FC } from 'react'

import { InputWithValidation, MessagePopup } from '@skytech/ui'
import { Button as UIButton } from '@skytech/ui/Button'
import { getWhiteLabelForDealerStyle } from '@skytech/utils'
import CancelIcon from 'mdi-react/CancelIcon'
import TrayArrowDownIcon from 'mdi-react/TrayArrowDownIcon'
import TruckCheckIcon from 'mdi-react/TruckCheckIcon'
import { useDispatch, useSelector } from 'react-redux'

import { useAppContext } from '@/App.context'
import Button from '@/components/form/Button'
import { Translation } from '@/components/i18n/Translation'
import {
  getEcomOrderDeliveryDate,
  getEcomOrderVendorSalesNo,
  getEcomOrderStatus,
  getIsLoading,
  setEcomOrderStatus,
  setDealerComment,
  getEcomOrderDealerComment,
} from '@/ducks/ecomOrderReducer'
import { ECOM_STATUS } from '@/util/orders'

import { EDITABLE_STATUSES } from './constants'

const EcomOrderPageOptions: FC<{
  isViewOnlyMode: boolean
  onSetIsViewOnlyMode: (val: boolean) => void
}> = ({ isViewOnlyMode, onSetIsViewOnlyMode }) => {
  const { whiteLabel } = useAppContext()
  const status = useSelector(getEcomOrderStatus)
  const dispatch = useDispatch()
  const isLoading = useSelector(getIsLoading)

  const [isConfirmPickingOrderOpen, setIsConfirmPickingOrderOpen] = useState(false)

  return (
    <div className="order-options">
      {EDITABLE_STATUSES.includes(status) && (
        <>
          <DeliverOrder isViewOnlyMode={isViewOnlyMode} />
          <CancelOrder isViewOnlyMode={isViewOnlyMode} />
        </>
      )}

      {isViewOnlyMode && (
        <div className="order-options__option">
          <Button
            Icon={TrayArrowDownIcon}
            onClick={() => {
              setIsConfirmPickingOrderOpen(true)
            }}
          >
            <Translation id="Pick order for processing" />
          </Button>
        </div>
      )}

      <MessagePopup
        isOpen={isConfirmPickingOrderOpen}
        message={<Translation id="Are you sure you want to pick this order for processing?" />}
        title={<Translation id="Pick order for processing" />}
        whiteLabel={getWhiteLabelForDealerStyle(whiteLabel)}
      >
        <footer className="mt-3 flex justify-end gap-4 !bg-transparent !p-0">
          <Button
            className="mb-0"
            variant="tertiary"
            onClick={() => setIsConfirmPickingOrderOpen(false)}
          >
            <Translation id="Cancel" />
          </Button>

          <Button
            className="mb-0"
            isLoading={isLoading}
            onClick={() => {
              dispatch(
                // @ts-ignore
                setEcomOrderStatus(ECOM_STATUS.ACCEPTED, () => {
                  setIsConfirmPickingOrderOpen(false)
                  onSetIsViewOnlyMode(false)
                })
              )
            }}
          >
            <Translation id="Pick order for processing" />
          </Button>
        </footer>
      </MessagePopup>
    </div>
  )
}
const DeliverOrder: FC<{ isViewOnlyMode: boolean }> = ({ isViewOnlyMode }) => {
  const dispatch = useDispatch()
  const isLoading = useSelector(getIsLoading)
  const deliveryDate = useSelector(getEcomOrderDeliveryDate)
  const vendorSalesOrderNo = useSelector(getEcomOrderVendorSalesNo)

  return (
    <div className="order-options__option">
      <Button
        Icon={TruckCheckIcon}
        disabled={isLoading || !deliveryDate || !vendorSalesOrderNo || isViewOnlyMode}
        // @ts-ignore
        onClick={() => dispatch(setEcomOrderStatus(ECOM_STATUS.DELIVERED))}
      >
        <Translation id="Order Delivered" />
      </Button>
    </div>
  )
}

const CancelOrder: FC<{ isViewOnlyMode: boolean }> = ({ isViewOnlyMode }) => {
  const { whiteLabel } = useAppContext()
  const dispatch = useDispatch()
  const isLoading = useSelector(getIsLoading)
  const [isConfirmationPopupOpen, setIsConfirmationPopupOpen] = useState(false)
  const dealerComment = useSelector(getEcomOrderDealerComment)

  return (
    <div className="order-options__option">
      <Button
        Icon={CancelIcon}
        disabled={isLoading || isViewOnlyMode}
        onClick={() => setIsConfirmationPopupOpen(true)}
      >
        <Translation id="Cancel Order" />
      </Button>

      <MessagePopup
        isOpen={isConfirmationPopupOpen}
        message={<Translation id="Are you sure you want to cancel this order?" />}
        title={<Translation id="Cancel order" />}
        whiteLabel={getWhiteLabelForDealerStyle(whiteLabel)}
      >
        <div>
          <InputWithValidation
            id="dealer_comment"
            marginBottom={16}
            label={<Translation id="Your comment" />}
            value={dealerComment || ''}
            validator={val => Boolean(val)}
            errorMessage={<Translation id="Please provide a reason for cancelling the order" />}
            onChange={e => dispatch(setDealerComment(e.target.value))}
            whiteLabel={getWhiteLabelForDealerStyle(whiteLabel)}
          />

          <footer className="mt-3 flex justify-end !bg-transparent !p-0">
            <UIButton
              className="mb-0"
              variant="tertiary"
              onClick={() => setIsConfirmationPopupOpen(false)}
            >
              <Translation id="Close" />
            </UIButton>

            <UIButton
              className="mb-0"
              isLoading={isLoading}
              disabled={!dealerComment}
              // @ts-ignore
              onClick={() => dispatch(setEcomOrderStatus(ECOM_STATUS.CANCELED))}
            >
              <Translation id="Cancel order" />
            </UIButton>
          </footer>
        </div>
      </MessagePopup>
    </div>
  )
}

export default EcomOrderPageOptions
